import axios, { type AxiosPromise } from 'axios';
import { ref, type Ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import parseLinkHeader from '~/utils/parseLinkHeader';
import { catchRequestError } from '~/utils/request';
import type { CursorPaginationResponse } from '~/services/api';
import type { CursorPaginationParams } from '~/services/BaseClient.types';

export const useCursorPagination = <T, P extends CursorPaginationParams = CursorPaginationParams>() => {
    const error = ref('');
    const loading = ref(false);
    const list: Ref<T[]> = ref([]);
    const fetchParams = ref<P>({} as P);
    const toasted = useToasted();
    const isLast = ref(false);

    let latestRequestId = 0;

    const fetchRecords = async (fetchRequest: AxiosPromise<CursorPaginationResponse<T>>): Promise<T[] | undefined> => {
        const currentRequestId = ++latestRequestId;
        loading.value = true;
        error.value = '';

        try {
            const response = await fetchRequest;

            if (currentRequestId !== latestRequestId) {
                return;
            }

            const data = response.data.data;
            list.value = !fetchParams.value.cursor ? response.data.data : [...list.value, ...data];

            if (response.headers?.link) {
                const nextOrPrev =
                    fetchParams.value.forward == undefined || fetchParams.value.forward ? 'next' : 'prev';
                const cursorLink = parseLinkHeader(response.headers?.link)?.[nextOrPrev];

                fetchParams.value = {
                    ...fetchParams.value,
                    cursor: cursorLink?.cursor,
                };
            } else {
                const nextPageParams = new URLSearchParams(response.data.next_page_url?.split('?')[1]);
                fetchParams.value = {
                    ...fetchParams.value,
                    cursor: nextPageParams.get('cursor'),
                };
            }

            isLast.value = !fetchParams.value.cursor;

            return data;
        } catch (e) {
            if (!axios.isCancel(e)) {
                toasted.global.platform_error();
                error.value = catchRequestError(e);
            }
        } finally {
            if (currentRequestId === latestRequestId) {
                loading.value = false;
            }
        }
    };

    return { list, loading, fetchParams, fetchRecords, isLast, error };
};
